import { useTranslation } from "react-i18next";

import { PlusIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ButtonLink } from "simplydo/react/ui/button";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H2 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";

import { useCurrentUser } from "utils/currentUser";

import IdeaCardView from "components/lib/Ideas/IdeaView/IdeaCardView";
import type { LibIdeaViewIdea } from "components/lib/Ideas/types";
import { EmptyBox } from "components/lib/UI";

import type { PublicUser, PublicUserIdea } from "./types";

type ProfileIdeasProps = {
  ideas: PublicUserIdea[];
  loading: boolean;
  filter: string;
  sort: string;
  showDraft: boolean;
  showSubmitted: boolean;
  profileUser: PublicUser;
};

const ProfileIdeas = ({
  ideas = [],
  loading,
  filter,
  sort,
  showDraft,
  showSubmitted,
  profileUser,
}: ProfileIdeasProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();

  const filteredIdeas = ideas.filter((idea) => {
    if (!showDraft && !idea.isSubmitted) return false;
    if (!showSubmitted && idea.isSubmitted) return false;
    if (
      idea.ownerTags?.find((fullTag) =>
        fullTag.value.match(RegExp(filter, "i")),
      )
    )
      return true;
    if (idea.name?.match(RegExp(filter, "i"))) return true;
    if (idea.ownerChallenge?.name?.includes(filter)) return true;
    return false;
  });
  if (sort === "oldest") filteredIdeas.reverse();
  if (loading) return <Loader />;
  if (!loading && !filteredIdeas.length && !ideas.length) {
    return (
      <EmptyBox>
        <H2>
          {t(
            user?._id === profileUser?._id
              ? "ideas.yours.sidebar.noneCreated"
              : "ideas.others.noneCreated",
            { user: profileUser?.profile?.fullName },
          )}
        </H2>
        <ButtonLink color="primary" variant="outline" to="/challenges/discover">
          <Icon icon={PlusIcon} />
          {t("challenges.discover.title")}
        </ButtonLink>
      </EmptyBox>
    );
  }
  if (!loading && !filteredIdeas.length && ideas.length > 0) {
    return <EmptyBox title="This user has no public ideas available" />;
  }
  return (
    <div style={{ marginBottom: 20 }}>
      {profileUser?._id === user?._id && (
        <div style={{ marginBottom: 15 }}>
          <Message>{t("users.profile.ideas.admins")}</Message>
        </div>
      )}
      <Grid columns={3} mobile={1}>
        {filteredIdeas.map((idea) =>
          idea.ownerChallenge ? (
            <GridColumn key={idea._id}>
              <IdeaCardView
                idea={idea as LibIdeaViewIdea}
                challenge={idea.ownerChallenge}
                ideasVotedFor={0}
              />
            </GridColumn>
          ) : null,
        )}
      </Grid>
    </div>
  );
};

export default ProfileIdeas;
